/* ============================================================
   Cascadia Overland Co. - Frontier story section
   assets/frontier-story.js
   Pairs with sections/frontier-story.liquid and
   assets/frontier-story.css.

   Loaded with defer. Initialises every .co-frontier root on the
   page, and re-initialises when the theme editor swaps the
   section in (shopify:section:load).
   ============================================================ */
(function () {

  /* ---------- 1. Viewport width without the scrollbar ----------
     Feeds the full-bleed breakout in the CSS. Using clientWidth
     instead of 100vw stops Windows browsers from gaining a
     horizontal scrollbar. Page-level, so it runs once.        */
  function setVW() {
    document.documentElement.style.setProperty(
      '--co-vw', document.documentElement.clientWidth + 'px'
    );
  }
  setVW();
  window.addEventListener('resize', setVW);
  window.addEventListener('orientationchange', setVW);

  /* Height of a fixed or sticky header, so anchor jumps and
     programmatic scrolls do not land under it.                */
  function stickyOffset() {
    var pad = 16;
    var candidates = document.querySelectorAll('header, .header, #shopify-section-header, sticky-header');
    for (var i = 0; i < candidates.length; i++) {
      var pos = window.getComputedStyle(candidates[i]).position;
      if (pos === 'fixed' || pos === 'sticky') {
        return candidates[i].getBoundingClientRect().height + pad;
      }
    }
    return pad;
  }

  function scrollToEl(target) {
    var y = target.getBoundingClientRect().top + window.pageYOffset - stickyOffset();
    window.scrollTo({
      top: y,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  function init(root) {
    if (!root || root.dataset.coInit === '1') return;
    root.dataset.coInit = '1';

    /* ---------- 2. Scroll to the Configure Your Camper block ----------
       Looks for the heading by its text, then falls back to the
       add-to-cart form, then to the main product section. Change
       the data-co-configure-text attribute on the root if that
       heading is ever renamed.                                 */
    var CONFIG_TEXT = (root.getAttribute('data-co-configure-text') || 'configure your camper').toLowerCase();

    function findConfigureTarget() {
      var heads = document.querySelectorAll('h1,h2,h3,h4,h5,h6,legend');
      for (var i = 0; i < heads.length; i++) {
        if (root.contains(heads[i])) continue;
        if (heads[i].textContent.trim().toLowerCase().indexOf(CONFIG_TEXT) === 0) {
          return heads[i];
        }
      }
      return document.querySelector('form[action*="/cart/add"]')
          || document.querySelector('#MainProduct, .product__info-wrapper, product-info');
    }

    root.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-co-configure]');
      if (!trigger) return;
      var target = findConfigureTarget();
      if (!target) return;
      e.preventDefault();
      scrollToEl(target);
    });

    /* ---------- 2a. In-page anchors ----------
       Any link to #frontier-fans and friends scrolls smoothly
       and clears a sticky header. Links from outside this
       section still work on their own via the scroll-margin-top
       rule in the CSS.                                         */
    root.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link || link.hasAttribute('data-co-configure')) return;
      var id = link.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      scrollToEl(target);
      if (history.replaceState) history.replaceState(null, '', '#' + id);
    });

    /* ---------- 2b. Setup video, tap to play ----------
       preload="none" means the browser fetches nothing until the
       first tap. On a ~30 MB clip that is the difference between
       every visitor paying for the file and only the interested
       ones paying for it.

       Playback only ever starts from a real user gesture, so
       there is no autoplay policy to fight. The observer pauses
       on scroll-away and never starts anything.                */
    var vidBox = root.querySelector('.co-video');
    var vid    = vidBox && vidBox.querySelector('video');
    var vidBtn = vidBox && vidBox.querySelector('.co-video-toggle');

    if (vid && vidBtn && vidBox) {
      var everPlayed = false;

      vid.muted = true;
      vid.setAttribute('muted', '');

      var setBusy = function (on) {
        vidBox.classList.toggle('is-busy', !!on);
      };

      var paintBtn = function () {
        var paused = vid.paused;
        vidBtn.innerHTML = paused ? '&#9654;' : '&#10074;&#10074;';
        vidBtn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
        vidBtn.classList.toggle('is-idle', paused && !everPlayed);
        vidBox.classList.toggle('is-started', everPlayed);
      };

      var toggle = function () {
        if (vid.paused) {
          if (!everPlayed) setBusy(true);
          var attempt = vid.play();
          if (attempt && attempt.then) {
            attempt.then(function () {
              everPlayed = true;
              setBusy(false);
              paintBtn();
            }).catch(function () {
              setBusy(false);
              paintBtn();
            });
          } else {
            everPlayed = true;
            setBusy(false);
          }
        } else {
          vid.pause();
        }
        paintBtn();
      };

      paintBtn();

      /* One handler on the box. The button sits inside it, so a
         tap on either the poster or the button lands here once. */
      vidBox.addEventListener('click', toggle);

      vid.addEventListener('play', paintBtn);
      vid.addEventListener('pause', paintBtn);
      vid.addEventListener('waiting', function () { setBusy(true); });
      vid.addEventListener('playing', function () {
        everPlayed = true;
        setBusy(false);
        paintBtn();
      });

      vid.addEventListener('error', function () {
        setBusy(false);
        vidBox.style.background = 'transparent';
        if (window.console) {
          console.warn('Frontier setup video failed', vid.error && vid.error.code);
        }
      });

      /* Pause only. Never starts playback, and leaves an
         untouched video alone so preload="none" holds.         */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting && everPlayed && !vid.paused) vid.pause();
          });
        }, { threshold: 0.25 }).observe(vid);
      }
    }

    /* ---------- 3. Scroll reveal ---------- */
    var items = root.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach(function (el) { io.observe(el); });
  }

  function boot(scope) {
    var host = scope || document;
    if (host.classList && host.classList.contains('co-frontier')) init(host);
    host.querySelectorAll('.co-frontier').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor: section re-rendered, so init the new copy */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

})();
